/**
 * Support live-agent hours — client-side mirror.
 *
 * Live agents: Mon–Fri, 9:30 AM–4:30 PM Eastern (DST-aware via Intl).
 * "Report an issue" is asynchronous and always available — hours apply
 * only to the live-agent path.
 *
 * This is a client-side second source of truth until Amazon Connect's
 * Hours of Operation is wired. The constant is centralized here so the
 * eventual backend value has one place to replace.
 *
 * TODO (follow-up): serve hours from Thor so ops can change them without
 * a deploy. Holidays are not modeled — acceptable this slice.
 */

/** Eastern timezone hours for live-agent availability. */
export const SUPPORT_LIVE_AGENT_HOURS = {
  startHour: 9,
  startMinute: 30,
  endHour: 16,
  endMinute: 30,
  daysOfWeek: [1, 2, 3, 4, 5], // Mon–Fri (0=Sunday)
  timeZone: 'America/New_York',
} as const;

/** Display label for the support-hours subline (off-hours). */
export const SUPPORT_HOURS_LABEL = 'Mon–Fri, 9:30 AM–4:30 PM ET';

/**
 * Returns true if `now` falls within live-agent support hours
 * (Mon–Fri, 9:30 AM–4:30 PM Eastern, DST-aware).
 */
export function isWithinLiveAgentHours(now: Date = new Date()): boolean {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SUPPORT_LIVE_AGENT_HOURS.timeZone,
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).formatToParts(now);

  const values: Record<string, number> = {};
  for (const p of parts) {
    if (p.type !== 'literal') {
      values[p.type] = parseInt(p.value, 10);
    }
  }

  const weekdayPart = parts.find((p) => p.type === 'weekday');
  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  const etDay = weekdayPart ? weekdayMap[weekdayPart.value] ?? -1 : -1;

  if (
    etDay === -1 ||
    !(SUPPORT_LIVE_AGENT_HOURS.daysOfWeek as readonly number[]).includes(etDay)
  ) {
    return false;
  }

  const hour = values.hour ?? 0;
  const minute = values.minute ?? 0;
  const totalMinutes = hour * 60 + minute;

  const start =
    SUPPORT_LIVE_AGENT_HOURS.startHour * 60 +
    SUPPORT_LIVE_AGENT_HOURS.startMinute;
  const end =
    SUPPORT_LIVE_AGENT_HOURS.endHour * 60 + SUPPORT_LIVE_AGENT_HOURS.endMinute;

  return totalMinutes >= start && totalMinutes < end;
}

/**
 * Return a human-friendly label for when live agents will next be
 * available — e.g. "today at 9:30 AM ET" or "Monday at 9:30 AM ET".
 *
 * Only meaningful off-hours; callers should check isWithinLiveAgentHours
 * before calling this.
 */
export function nextOpenLabel(now: Date = new Date()): string {
  const H = SUPPORT_LIVE_AGENT_HOURS;
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: H.timeZone,
    weekday: 'short',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });

  // ET date components
  const dateParts = fmt.formatToParts(now);
  const dateValues: Record<string, number> = {};
  for (const p of dateParts) {
    if (p.type !== 'literal') {
      dateValues[p.type] = parseInt(p.value, 10);
    }
  }

  const etYear = dateValues.year ?? 0;
  const etMonth = (dateValues.month ?? 1) - 1; // JS months are 0-indexed
  const etDay = dateValues.day ?? 1;

  const weekdayPart = dateParts.find((p) => p.type === 'weekday');
  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  const etWeekday = weekdayPart ? weekdayMap[weekdayPart.value] ?? 0 : 0;

  // Get ET hour/minute
  const timeFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: H.timeZone,
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });
  const timeParts = timeFmt.formatToParts(now);
  const timeValues: Record<string, number> = {};
  for (const p of timeParts) {
    if (p.type !== 'literal') {
      timeValues[p.type] = parseInt(p.value, 10);
    }
  }
  const etHour = timeValues.hour ?? 0;
  const etMinute = timeValues.minute ?? 0;
  const totalMinutes = etHour * 60 + etMinute;
  const startMinutes = H.startHour * 60 + H.startMinute;
  const endMinutes = H.endHour * 60 + H.endMinute;

  // Determine the next-open ET date.
  let nextOpenDate = new Date(
    etYear,
    etMonth,
    etDay,
    H.startHour,
    H.startMinute
  );

  const isWeekday = (H.daysOfWeek as readonly number[]).includes(etWeekday);

  if (isWeekday && totalMinutes < startMinutes) {
    // Before open today — open is today.
    // nextOpenDate already set to today.
  } else if (isWeekday && totalMinutes >= endMinutes) {
    // After close — advance to next weekday.
    nextOpenDate = _nextWeekday(nextOpenDate);
  } else if (!isWeekday) {
    // Weekend — advance to Monday.
    nextOpenDate = _nextWeekday(nextOpenDate);
  }

  // Format the label.
  const nowDate = new Date(etYear, etMonth, etDay);
  const openDate = new Date(
    nextOpenDate.getFullYear(),
    nextOpenDate.getMonth(),
    nextOpenDate.getDate()
  );
  const diffDays = Math.round(
    (openDate.getTime() - nowDate.getTime()) / 86400000
  );

  if (diffDays === 0) {
    return 'today at 9:30 AM ET';
  }

  const weekdayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const openWeekday = weekdayNames[nextOpenDate.getDay()];
  return `${openWeekday} at 9:30 AM ET`;
}

/** Advance `date` to the next weekday (Mon–Fri), preserving the time. */
function _nextWeekday(date: Date): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + 1);
  while (
    !(SUPPORT_LIVE_AGENT_HOURS.daysOfWeek as readonly number[]).includes(
      d.getDay()
    )
  ) {
    d.setDate(d.getDate() + 1);
  }
  return d;
}
